/**
 * Man-Match ("MOD" — Man Only if Deep) as a responsibility spec.
 *
 * The reference migration from a hand-written policy to declarative data. Compare against the
 * prose in `.kiro/steering/cover4Match.md`: the corner reads his #1, carries him if he gets
 * vertical past ten yards, and passes him off to the underneath defenders if he breaks short.
 *
 * Used by CB1/CB2 (deep third or quarter) and by FS/SS, which differ only in params —
 * `readTowardZone` makes a safety drop straight back instead of mirroring, and `overTheTop`
 * stops him lurching up to trail. That both a corner and a safety are the same spec with
 * different tuning is the point: params remain the tuning seam, per the steering doc.
 */

import { ManMatchParams, Position } from '../../types';
import type { KeyObservables } from '../observables';
import { isBreak, isVerticalPush, landmarkFor } from '../observables';
import { ResponsibilitySpec } from '../responsibility';

/**
 * Thresholds for RE-ATTACHING to a key already passed off. Tuned to fire on a genuine second
 * vertical push while ignoring a route that merely broke off and kept running downfield at an
 * angle — a slant's lateral speed exceeds its vertical speed, so the ratio excludes it.
 */
export const REATTACH_MIN_VERTICAL_SPEED = 12;
export const REATTACH_MAX_LATERAL_RATIO = 0.6;
export const REATTACH_MIN_SPEED_FRACTION = 0.7;

/** The read line for THIS key: his own ten-yard landmark, widened by the tolerance. */
const readLineOf = (key: KeyObservables, p: ManMatchParams) =>
  landmarkFor(key, p.readDepthYards) + p.breakDepthToleranceY;

/**
 * Safeties (`readTowardZone`) drop continuously toward a FLOOR depth rather than seeking a
 * point, so the backpedal reads as unbroken motion instead of arrive-and-freeze. The landmark's
 * y is deliberately deeper than a normal play reaches.
 */
const zoneDrop = (p: ManMatchParams, speed: number) => ({
  target: { x: p.zoneLandmark.x, y: p.zoneLandmark.y } as Position,
  speed,
  technique: 'backpedal' as const,
});

export const MAN_MATCH_SPEC: ResponsibilitySpec<ManMatchParams> = {
  id: 'mod',
  initialTechnique: 'backpedal',

  latches: {
    /** He broke off clearly ABOVE the read line — a short route, not a vertical threat. */
    brokeShallow: ({ key, params }) =>
      isBreak(key, params.breakAngleDeg, params.settleSpeedFraction) &&
      key.position.y > readLineOf(key, params),
  },

  transitions: [
    {
      note: 'short break — pass him off and keep dropping',
      from: ['READ'],
      to: 'ZONE',
      when: ({ latches }) => latches.brokeShallow,
    },
    {
      note: 'crossed the read line still vertical — carry him',
      from: ['READ'],
      to: 'MAN',
      when: ({ key, params }) => key.position.y <= readLineOf(key, params),
    },
    {
      // A defender who passed his key off is NOT finished reading him — that assumption is
      // exactly what a double move is built to exploit. He owns the deep zone, so a key
      // climbing back through the read line is his problem again. Clearing the latch means a
      // receiver can double-move him twice.
      note: 'double move — he sold the break and took off; re-attach',
      from: ['ZONE'],
      to: 'MAN',
      when: ({ key, params }) =>
        key.position.y <= readLineOf(key, params) &&
        isVerticalPush(key, REATTACH_MIN_VERTICAL_SPEED, REATTACH_MAX_LATERAL_RATIO, REATTACH_MIN_SPEED_FRACTION),
      clearLatches: ['brokeShallow'],
    },
  ],

  behavior: {
    // Mirror the key laterally, holding or gaining depth to keep the cushion. Safeties instead
    // drop straight back — they are reading from depth, not matching feet.
    READ: ({ key, params, defender }) =>
      params.readTowardZone
        ? zoneDrop(params, params.readSpeed)
        : {
            target: { x: key.position.x, y: Math.min(defender.y, key.position.y - params.readCushion) },
            speed: params.readSpeed,
          },

    // Keep working to the deep zone at a controlled pace — don't sprint to a point and freeze.
    ZONE: ({ params }) =>
      params.readTowardZone
        ? zoneDrop(params, params.zoneDropSpeed)
        : { target: params.zoneLandmark, speed: params.zoneDropSpeed },

    // Trail the key. Over-the-top defenders never move shallower to do it — they hold their
    // depth and let him climb, so committing doesn't yank a safety up toward the receiver.
    MAN: ({ key, params, defender }) => {
      const trailY = key.position.y - params.trailCushion;
      return {
        target: { x: key.position.x, y: params.overTheTop ? Math.min(defender.y, trailY) : trailY },
        speed: params.maxSpeed,
      };
    },
  },
};
